import { useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './ui.tsx';

/**
 * A hover and focus tooltip.
 *
 * The native `title` attribute was doing this job and doing it badly: it waits
 * a second or two, renders in the OS style rather than the app's, never
 * appears for keyboard users, and cannot show a shortcut on its own line. For
 * a toolbar of small glyph buttons — which is most of the sheet toolbar —
 * knowing what a button does before pressing it is the difference between
 * usable and guesswork.
 *
 * Rendered through a portal so a tooltip on a control inside a clipped or
 * scrolling container is not cut off by it.
 */

interface Props {
  /** What the control does, in plain words. */
  label: string;
  /** Optional keyboard shortcut, shown beneath the label. */
  shortcut?: string;
  /** Longer explanation, for controls whose effect is not obvious. */
  detail?: string;
  placement?: Side;
  /** Alias for placement, for callers that read better this way. */
  side?: Side;
  children: ReactElement;
}

type Side = 'top' | 'bottom' | 'left' | 'right';

const SHOW_DELAY = 350;

/** Where the tooltip is anchored, and how it must shift to sit there. */
const OFFSET = 8;

/** How close to the window edge a tooltip may sit before it is nudged back. */
const MARGIN = 8;

export function Tooltip({ label, shortcut, detail, placement, side, children }: Props) {
  const at: Side = side ?? placement ?? 'bottom';
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    /** The trigger's edges, kept so the tooltip can flip across it. */
    anchorTop: number;
    anchorBottom: number;
  } | null>(null);
  const anchor = useRef<HTMLSpanElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const show = (immediate = false) => {
    const reveal = () => {
      const element = anchor.current?.firstElementChild ?? anchor.current;
      if (!element) return;
      const bounds = element.getBoundingClientRect();
      const placed =
        at === 'right'
          ? { top: bounds.top + bounds.height / 2, left: bounds.right + OFFSET }
          : at === 'left'
            ? { top: bounds.top + bounds.height / 2, left: bounds.left - OFFSET }
            : {
                top: at === 'bottom' ? bounds.bottom + OFFSET : bounds.top - OFFSET,
                left: bounds.left + bounds.width / 2,
              };

      setPosition({ ...placed, anchorTop: bounds.top, anchorBottom: bounds.bottom });
    };

    if (immediate) reveal();
    else {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(reveal, SHOW_DELAY);
    }
  };

  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    setPosition(null);
  };

  return (
    <>
      <span
        ref={anchor}
        className="contents"
        onPointerEnter={() => show()}
        onPointerLeave={hide}
        onPointerDown={hide}
        // Keyboard users get it instantly: they have already committed to the
        // control by tabbing to it, so a delay is only a delay.
        onFocusCapture={() => show(true)}
        onBlurCapture={hide}
        aria-describedby={position ? id : undefined}
      >
        {children}
      </span>

      {position
        ? createPortal(
            <div
              id={id}
              role="tooltip"
              className={cx(
                'pointer-events-none fixed z-[100] max-w-64 rounded-md',
                'bg-[var(--ink)] px-2.5 py-1.5 text-white shadow-lg',
                // Each side needs its own shift: horizontal placements centre
                // vertically, vertical ones centre horizontally.
                (at === 'top' || at === 'bottom') && '-translate-x-1/2',
                at === 'top' && '-translate-y-full',
                (at === 'left' || at === 'right') && '-translate-y-1/2',
                at === 'left' && '-translate-x-full',
              )}
              // Clamped to the viewport. Without this a tooltip on a control
              // near the right edge — the actions at the end of every row —
              // was centred on its trigger and pushed half off-screen, so the
              // text wrapped into a tall narrow column of two or three
              // characters. Clamping is done here rather than at measure time
              // because it needs the rendered width, which only exists now.
              ref={(node) => {
                if (!node) return;
                // Reset first: this runs again on every reveal, and last
                // time's nudge would otherwise compound.
                node.style.marginLeft = '';
                node.style.marginTop = '';

                const box = node.getBoundingClientRect();

                const overflowRight = box.right - (window.innerWidth - MARGIN);
                const overflowLeft = MARGIN - box.left;
                if (overflowRight > 0) node.style.marginLeft = `-${overflowRight}px`;
                else if (overflowLeft > 0) node.style.marginLeft = `${overflowLeft}px`;

                // Flip to the other side of the trigger rather than merely
                // sliding up: the profile block sits at the foot of the
                // sidebar, so a tooltip below it ran off the bottom of the
                // window and was cut in half. Sliding it up would cover the
                // very control being described.
                if (box.bottom > window.innerHeight - MARGIN) {
                  const flipped = position.anchorTop - OFFSET - box.height;
                  node.style.marginTop = `${Math.max(MARGIN - box.top, flipped - box.top)}px`;
                } else if (box.top < MARGIN) {
                  const flipped = position.anchorBottom + OFFSET;
                  node.style.marginTop = `${flipped - box.top}px`;
                }
              }}
              style={{ top: position.top, left: position.left }}
            >
              <span className="block text-micro font-medium leading-snug">{label}</span>
              {shortcut ? (
                <span className="mt-0.5 block font-mono text-[11px] leading-snug text-[var(--blue-200)]">
                  {shortcut}
                </span>
              ) : null}
              {detail ? (
                <span className="mt-1 block text-[11px] leading-snug text-[var(--blue-100)]">
                  {detail}
                </span>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Wraps children only when a label is given, so callers can stay declarative. */
export function MaybeTooltip({
  label,
  shortcut,
  children,
}: {
  label?: string | undefined;
  shortcut?: string | undefined;
  children: ReactElement;
}): ReactNode {
  if (!label) return children;
  return (
    <Tooltip label={label} {...(shortcut ? { shortcut } : {})}>
      {children}
    </Tooltip>
  );
}
